import { supabase, tables } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';
import { StaffMember } from '@/types/staffTypes';

export const createStaffMember = async (
  staff: Omit<StaffMember, 'id' | 'created_at'>
): Promise<StaffMember | null> => {
  try {
    // Get current user and their organization_id
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('No authenticated user found');
    }

    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (profileError || !profile?.organization_id) {
      throw new Error('Could not retrieve organization ID from user profile');
    }

    // Construct explicit payload with all required fields
    const payload = {
      first_name: staff.first_name,
      last_name: staff.last_name,
      email: staff.email || null,
      phone: staff.phone || null,
      role: staff.role || 'instructor',
      is_active: true,
      profile_photo_url: null,
      organization_id: profile.organization_id,
      start_date: staff.start_date || null
    };

    console.log("📦 Insert payload:", payload);
    
    const { data, error } = await tables.staff_members()
      .insert([payload])
      .select()
      .single();

    if (error) {
      console.error("❌ Insert error:", error);
      throw error;
    }

    console.log("✅ Insert successful:", data);
    return data as StaffMember;
  } catch (error) {
    handleError(
      error,
      "Failed to create staff member",
      ErrorSeverity.ERROR,
      { operation: 'createStaffMember' }
    );
    return null;
  }
};

export const updateStaffMember = async (id: string, staff: Partial<StaffMember>): Promise<StaffMember | null> => {
  try {
    // Allow all role updates for bizadmin (enforced by RLS)
    const { data, error } = await tables.staff_members()
      .update(staff)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      const context: ErrorContext = {
        operation: 'updateStaffMember',
        additionalInfo: { id }
      };
      handleError(error, "Failed to update staff member", ErrorSeverity.ERROR, context);
      return null;
    }

    return data as StaffMember;
  } catch (error) {
    const context: ErrorContext = {
      operation: 'updateStaffMember',
      additionalInfo: { id }
    };
    handleError(error, "Failed to update staff member", ErrorSeverity.ERROR, context);
    return null;
  }
};

export const deleteStaffMember = async (id: string): Promise<boolean> => {
  try {
    const { error } = await tables.staff_members()
      .delete()
      .eq('id', id);

    if (error) {
      const context: ErrorContext = {
        operation: 'deleteStaffMember',
        additionalInfo: { id }
      };
      handleError(error, "Failed to delete staff member", ErrorSeverity.ERROR, context);
      return false;
    }

    return true;
  } catch (error) {
    const context: ErrorContext = {
      operation: 'deleteStaffMember',
      additionalInfo: { id }
    };
    handleError(error, "Failed to delete staff member", ErrorSeverity.ERROR, context);
    return false;
  }
};
